'use client';

import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Database, FileText, Radio } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getFileHistory } from './api';
import { formatBytes, formatTimestamp } from './tree';
import { useWorkspace } from './workspace-context';

function rec(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
}
function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

export function WorkspaceDetailsTab({ sandboxOutput }: { sandboxOutput?: unknown }) {
  const { resolvePhase, resolveError, workspace, files, selectedFile } = useWorkspace();

  const [writtenBy, setWrittenBy] = useState<string | null>(null);
  useEffect(() => {
    setWrittenBy(null);
    if (!workspace || !selectedFile) return;
    let cancelled = false;
    getFileHistory(workspace.id, selectedFile.path).then((res) => {
      if (cancelled) return;
      if (res.ok && res.data.versions?.length) {
        setWrittenBy(res.data.versions[0].writtenBy ?? null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [workspace, selectedFile]);

  if (resolvePhase === 'loading' || resolvePhase === 'idle') {
    return (
      <div className="flex h-full items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
        <Spinner className="size-4" /> Opening workspace…
      </div>
    );
  }

  if (resolvePhase !== 'ready' || !workspace) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <div className="max-w-sm">
          <Database className="mx-auto size-6 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">Workspace details not available yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {resolveError ?? 'Details will appear once the workspace store is online.'}
          </p>
        </div>
      </div>
    );
  }

  const totalBytes = files.reduce((s, f) => s + (Number.isFinite(f.size) ? f.size : 0), 0);
  const out = rec(sandboxOutput);
  const sandbox = rec(out?.sandbox);
  const sandboxName = str(sandbox?.name) ?? str(out?.name);
  const sandboxState = str(sandbox?.state);

  return (
    <div className="h-full min-h-0 space-y-4 overflow-auto p-4">
      {/* Workspace summary */}
      <section className="rounded-lg border bg-background p-4">
        <div className="flex items-center gap-2">
          <Database className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Workspace</h3>
          <Badge variant="secondary" className="ml-auto text-[10px]">
            {workspace.kind}
          </Badge>
        </div>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <Row label="Name">{workspace.name}</Row>
          <Row label="Files">{files.length}</Row>
          <Row label="Total size">{formatBytes(totalBytes)}</Row>
          <Row label="Created">{formatTimestamp(workspace.createdAt)}</Row>
          <Row label="ID">
            <span className="font-mono text-xs">{workspace.id}</span>
          </Row>
        </dl>
      </section>

      {/* Selected file metadata */}
      {selectedFile ? (
        <section className="rounded-lg border bg-background p-4">
          <div className="flex items-center gap-2">
            <FileText className="size-4 text-muted-foreground" />
            <h3 className="truncate text-sm font-semibold">{selectedFile.path.split('/').pop()}</h3>
            <Badge variant="outline" className="ml-auto text-[10px]">
              v{selectedFile.version}
            </Badge>
          </div>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <Row label="Path">
              <span className="break-all font-mono text-xs">{selectedFile.path}</span>
            </Row>
            <Row label="Type">{selectedFile.mime || '—'}</Row>
            <Row label="Size">{formatBytes(selectedFile.size)}</Row>
            <Row label="Version">{selectedFile.version}</Row>
            <Row label="Hash">
              <span className="font-mono text-xs">{selectedFile.contentHash?.slice(0, 16) || '—'}…</span>
            </Row>
            <Row label="Modified">{formatTimestamp(selectedFile.mtime)}</Row>
            <Row label="Updated">{formatTimestamp(selectedFile.updatedAt)}</Row>
            <Row label="Written by">{writtenBy ?? '—'}</Row>
          </dl>
        </section>
      ) : (
        <p className="px-1 text-sm text-muted-foreground">
          Select a file in the Files tab to see its full metadata.
        </p>
      )}

      {/* Live sandbox (secondary, honest) */}
      {(sandboxName || sandboxState) && (
        <section className="rounded-lg border border-dashed border-border/70 bg-muted/10 p-4">
          <div className="flex items-center gap-2">
            <Radio className="size-4 text-amber-500" />
            <h3 className="text-sm font-semibold">Live sandbox</h3>
            <span className="ml-auto text-[11px] text-muted-foreground">not yet synced</span>
          </div>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            {sandboxName && <Row label="Sandbox">{sandboxName}</Row>}
            {sandboxState && <Row label="State">{sandboxState}</Row>}
          </dl>
        </section>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-foreground">{children}</dd>
    </>
  );
}
